import { FC, MouseEvent, ReactNode, useMemo, useState } from 'react';

import VerticalExpander from 'app/components/animations/VerticalExpander';
import { EvtKeys } from 'app/types';

import MenuItem from './MenuItem';
import KeyCodeListener from '../EventListeners/KeyCodeListener';
import Flex from '../Flex';
import Icon from '../Icon';
import Legal from '../Text/Legal';
import Paragraph from '../Text/Paragraph';

// Local Typings
interface Option {
  disabledReason?: null | string;
  italic?: boolean;
  label: ReactNode;
  testId?: string;
  value: number | string;
}
type MultiSelectProps = {
  value: null | number | string;
  values?: never;
} | {
  value?: never;
  values: number[] | string[];
};
export type ListProps = MultiSelectProps & {
  onChange: (id: number | string) => void;
  options: Option[];
  shouldDisableSelectOnSpace?: boolean;
  shouldStopPropagation?: boolean;
};

// Component Definition
const List: FC<ListProps> = ({
  onChange,
  options,
  shouldDisableSelectOnSpace,
  shouldStopPropagation,
  value,
  values,
}) => {
  const initialSelectedIndexes = useMemo(() => {
    if (value) {
      return [options.findIndex(option => option.value === value)];
    }

    if (values) {
      return values.map(val => options.findIndex(option => option.value === val));
    }

    return [];
  },
  [options, value, values]);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);

  const handleClick = ({
    id,
    index,
    isDisabled,
  }: {
    id: number | string;
    index: number;
    isDisabled: boolean;
  }) => (evt: MouseEvent) => {
    if (shouldStopPropagation) {
      evt.stopPropagation();
    }

    if (!isDisabled) {
      onChange(id);
    } else {
      setFocusedIndex(index);
    }
  };

  function handleDown() {
    const newFocusedIndex = focusedIndex + 1;
    setFocusedIndex(newFocusedIndex % options.length);
  }

  function handleUp() {
    let newFocusedIndex = focusedIndex - 1;
    if (newFocusedIndex < 0) {
      newFocusedIndex = options.length - 1;
    }
    setFocusedIndex(newFocusedIndex % options.length);
  }

  function handleSelect(evt: KeyboardEvent) {
    evt.preventDefault();

    const option = options[focusedIndex];
    if (option && !option.disabledReason) {
      onChange(option.value);
    }
  }

  return (
    <KeyCodeListener
      keyCodeActions={[
        {
          action: handleDown,
          key: EvtKeys.Down,
        },
        {
          action: handleUp,
          key: EvtKeys.Up,
        },
        {
          action: handleSelect,
          key: EvtKeys.Enter,
        },
        ...(shouldDisableSelectOnSpace
          ? []
          : [{
            action: handleSelect,
            key: EvtKeys.Space,
          }]),
      ]}
    >
      <ul className="menu_list">
        {options.map((option, index) => {
          const isSelected = initialSelectedIndexes.includes(index);
          const isFocused = focusedIndex === index;

          return (
            <VerticalExpander
              isExpanded
              key={option.value}
              transitionSpeedInMs={200}
            >
              <MenuItem
                isDisabled={!!option.disabledReason}
                isFocused={isFocused}
                isSelected={isSelected}
                onClick={handleClick({
                  id: option.value,
                  index,
                  isDisabled: !!option.disabledReason,
                })}
                testId={option.testId}
              >
                <Flex alignItems="flex-start">
                  {isFocused && option.disabledReason && (
                    <Icon
                      color="disabled"
                      name="block"
                    />
                  )}
                  <Flex
                    alignItems="flex-start"
                    flexDirection="column"
                    marginLeft={2}
                  >
                    <Paragraph
                      as="span"
                      fontColor="inherit"
                      italic={option.italic}
                      noWrap
                    >
                      {option.label}
                    </Paragraph>
                    {isFocused && option.disabledReason && (
                      <Legal
                        as="span"
                        fontColor="inherit"
                        italic={option.italic}
                        noWrap
                      >
                        {option.disabledReason}
                      </Legal>
                    )}
                  </Flex>
                </Flex>
              </MenuItem>
            </VerticalExpander>
          );
        })}
      </ul>
    </KeyCodeListener>
  );
};

// Exports
export default List;
